<script lang="ts">
	import type { SvelteAsr } from 'types/common'
	import type { ComponentProps } from 'svelte'
	import type { i18n } from 'types/common'
	import type { ServerInformation$result, StoresForLogin$result } from '$houdini'

	import { getContext, onMount } from 'svelte'
	import Login from '@isoftdata/svelte-login'
	import Button from '@isoftdata/svelte-button'
	import debugLog from 'debug'
	import sessionStore from 'stores/session'
	import { CloseSessionMutationStore, graphql } from '$houdini'
	import { makeLoginRequest } from 'utility/login-helper'
	import { startOAuthFlow } from 'utility/oauth-helper'
	import { buildVersion, buildDate, buildEnv } from 'utility/constants'

	type ServerInformation = ServerInformation$result['serverInformation']
	type SiteList = NonNullable<StoresForLogin$result['storesForLogin']>

	interface Props {
		username?: string
		isLoading?: boolean
		rememberMe?: boolean
		loadStores?: Promise<SiteList>
		serverInformation: Promise<ServerInformation>
		asr: SvelteAsr
	}

	let {
		loadStores,
		username = $bindable(''),
		isLoading = $bindable(false),
		serverInformation,
		rememberMe = false,
		asr,
	}: Props = $props()

	let loginComponent: Login | undefined = $state()
	let storeList: SiteList = $state([])
	let selectedStoreId: number | undefined = $state()

	const { t: translate } = getContext<i18n>('i18next')

	// #region GraphQL Queries/Mutations
	const closeSessionMutation: CloseSessionMutationStore = graphql`
		mutation CloseSessionMutation {
			closeSession
		}
	`
	// #endregion GraphQL Queries/Mutations

	function saveRememberMe(rememberMe, username) {
		if (rememberMe) {
			localStorage.setItem('lastUsername', username)
		} else {
			localStorage.removeItem('lastUsername')
		}
	}

	async function login(ctx: Parameters<ComponentProps<typeof Login>['login']>[0]) {
		const { username, password, rememberMe, siteId } = ctx
		debugLog('login fired')
		isLoading = true
		try {
			if (!siteId) {
				throw new Error(translate('login.noStoreSelectedAlert', 'Please select a store to login'))
			}

			await makeLoginRequest({
				username,
				password,
				storeId: siteId,
			})

			localStorage.setItem('lastStoreId', siteId?.toString() || '')
			saveRememberMe(rememberMe, username)

			debugLog(String($sessionStore))

			/* check if user has WEB_CAN_LOGIN*/
			if ($sessionStore.sessionPermissions?.WEB_CAN_LOGIN) {
				asr.go('app')
			} else {
				//need to cancel session
				await closeSessionMutation.mutate(null)
				sessionStore.set({})
				isLoading = false
				loginComponent?.invalidLogin(
					translate(
						'login.userDoesNotHaveLoginPermissionForEnterpriseWeb',
						'User does not have login permission for Enterprise Web',
					),
				)
			}
		} catch (err) {
			isLoading = false
			console.log(err)
			if (Array.isArray(err)) {
				const alert = err.reduce(
					(acc: { message: string; subMessages: Array<string> }, e, index) => {
						if (index === 0) {
							acc.message = e.message
						} else {
							acc.subMessages.push(e.message)
						}
						return acc
					},
					{ message: '', subMessages: [] },
				)

				loginComponent?.setAlert(alert)
			} else if ((err as Error)?.message) {
				loginComponent?.invalidLogin(
					(err as Error)?.message || translate('login.anUnknownErrorOccured', 'An unknown error occured'),
				)
			}
		}
	}

	onMount(async () => {
		try {
			isLoading = true
			storeList = (await loadStores) ?? []
			isLoading = false
		} catch (err) {
			loginComponent?.setAlert({
				message: err instanceof Error ? err.message : translate('login.fetchError', 'Failed to fetch stores.'),
				color: 'danger',
			})
		}
	})
</script>

<Login
	forgotPasswordDisabled
	{rememberMe}
	{isLoading}
	{login}
	siteLabel={translate('common:store', 'Store')}
	siteList={storeList}
	logo={{ light: 'images/ITrackEnterpiseOnTopNut.svg', dark: 'images/ITrackEnterpiseOnTopNut.svg' }}
	showLanguagePicker={import.meta.env.DEV}
	bind:username
	bind:selectedSiteId={selectedStoreId}
	bind:this={loginComponent}
>
	{#if import.meta.env.OAUTH_ENABLED === 'true'}
		<div class="mt-3 d-flex flex-column align-items-center">
			<hr class="w-100" />
			<Button
				outline
				color="secondary"
				class="w-100"
				onclick={startOAuthFlow}
			>
				Login with ISoft OAuth
			</Button>
		</div>
	{/if}
	<div class="mt-3 d-flex justify-content-center">
		<a
			href="https://changelog.isoftdata.com/?product=enterprise-web#v{buildVersion}"
			target="enterprise_web_changelog"
		>
			<small
				class="text-dim"
				title="Built Date: {buildDate}
Enviroment: {buildEnv}">{translate('login.appVersion', 'App Version')} {buildVersion}</small
			>
		</a>
	</div>
	<div class="d-flex flex-column align-items-center">
		{#await serverInformation then info}
			{#if info.releaseVersionNumber}
				<a
					href="https://changelog.isoftdata.com/?product=enterprise-api#v{info.releaseVersionNumber}"
					target="enterprise_api_changelog"
				>
					<small
						class="text-dim"
						title={info.schemaVersion ? `Schema Version ${info.schemaVersion}` : ''}
						>{translate('login.apiVersion', 'API Version')} {info.releaseVersionNumber}</small
					>
				</a>
			{/if}
		{/await}
	</div>
</Login>
